import React from 'react';
import { Drawer, Form, Input, Select, Button, message, Upload } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import './CreateTemplateDrawer.css';
import { padServices } from '../../../../api';
import { TEMPLATE_CATEGORIES } from '../..';
import CompressionConfirmModal from '../../../ImageCompressionModal/CompressionConfirmModal';
import {
  DEFAULT_COMPRESS_THRESHOLD_BYTES,
  isUploadCancelledError,
  uploadImageWithOptionalCompression,
} from '../../../../utils/imageUpload';

/** Above global `.ant-drawer` / `.ant-modal` z-index (see `index.css`). */
const CREATE_TEMPLATE_COMPRESS_MODAL_Z = 10000001;

export default function CreateTemplateDrawer({ open, onClose, onCreate }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [fileList, setFileList] = React.useState([]);
  const [thumbnailUrl, setThumbnailUrl] = React.useState('');
  const [isThumbnailUploading, setIsThumbnailUploading] = React.useState(false);
  const [compressConfirmState, setCompressConfirmState] = React.useState({
    open: false,
    fileSize: 0,
    resolver: null,
  });
  const categories = TEMPLATE_CATEGORIES;

  React.useEffect(() => {
    if (!open) {
      form.resetFields();
      setThumbnailUrl('');
      setFileList([]);
    }
  }, [open, form]);

  const handleUploadChange = ({ fileList: newFileList }) => {
    if (newFileList.length === 0) {
      setFileList([]);
      setThumbnailUrl('');
    }
  };

  const askToCompress = (file) =>
    new Promise((resolve) => {
      setCompressConfirmState({
        open: true,
        fileSize: file.size,
        resolver: resolve,
      });
    });

  const resolveCompressChoice = (shouldCompress) => {
    setCompressConfirmState((prev) => {
      if (prev.resolver) prev.resolver(shouldCompress);
      return { open: false, fileSize: 0, resolver: null };
    });
  };

  const beforeUpload = async (file) => {
    try {
      setIsThumbnailUploading(true);

      if (file.size <= DEFAULT_COMPRESS_THRESHOLD_BYTES) {
        message.loading({ content: 'Uploading thumbnail...', key: 'template-thumbnail-upload' });
      }

      const uploadedUrl = await uploadImageWithOptionalCompression(file, {
        thresholdBytes: DEFAULT_COMPRESS_THRESHOLD_BYTES,
        promptCompress: async (largeFile) => {
          message.destroy('template-thumbnail-upload');
          const choice = await askToCompress(largeFile);
          if (choice === null) {
            return null;
          }
          message.loading({
            content: choice ? 'Compressing and uploading thumbnail...' : 'Uploading original thumbnail...',
            key: 'template-thumbnail-upload',
          });
          return choice;
        },
      });

      setThumbnailUrl(uploadedUrl);
      setFileList([
        {
          uid: file.uid || `${Date.now()}`,
          name: file.name,
          status: 'done',
          url: uploadedUrl,
        },
      ]);
      message.success({ content: 'Thumbnail uploaded successfully!', key: 'template-thumbnail-upload' });
    } catch (error) {
      if (isUploadCancelledError(error)) {
        message.destroy('template-thumbnail-upload');
        return false;
      }
      setFileList([]);
      setThumbnailUrl('');
      message.error({ content: error?.message || 'Failed to upload thumbnail', key: 'template-thumbnail-upload' });
    } finally {
      setIsThumbnailUploading(false);
    }

    return false;
  };

  const tagRender = ({ label, closable, onClose }) => {
    const display = typeof label === 'string' && label.length > 10 ? label.slice(0, 10) + '…' : label;
    return (
      <span className="ctd-select-tag">
        {display}
        {closable && (
          <span className="ctd-select-tag-close" onClick={onClose}>×</span>
        )}
      </span>
    );
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const payload = {
        name: values.name.trim(),
        milepadData: '{}',
        isActive: values.isActive === 'true' || values.isActive === true,
        sort: 0,
        thumbnail: thumbnailUrl || 'string',
        docType: 'milepad',
        category: values.categories || []
      };
      const res = await padServices.createTemplate(payload);
      const createdTemplate = res?.data?.data;
      const templateId = createdTemplate?._id;

      message.success('Template created successfully');
      onCreate?.();
      onClose();

      if (templateId) {
        window.open(`/template/${templateId}`, '_blank');
      }
    } catch (error) {
      if (error?.errorFields) return; // validation error, do nothing
      const msg = error?.response?.data?.message || 'Failed to create template';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const FooterBar = (
    <div className="ctd-bar">
      <Button
        type="primary"
        size="large"
        block
        className="ctd-create-btn"
        loading={loading || isThumbnailUploading}
        disabled={loading || isThumbnailUploading}
        onClick={handleSubmit}
      >
        Create Template
      </Button>
    </div>
  );

  return (
    <Drawer
      className="ctd-drawer"
      title={<span style={{ fontWeight: 600, color: '#526bb1' }}>Create Template</span>}
      visible={open}
      onClose={onClose}
      width={640}
      destroyOnClose={false}
      styles={{ body: { background: '#f5f6f7', padding: 16 } }}
    >
      <div className="ctd" style={{ background: '#fff', borderRadius: 8, padding: '20px' }}>
        <CompressionConfirmModal
          open={compressConfirmState.open}
          fileSize={compressConfirmState.fileSize}
          zIndex={CREATE_TEMPLATE_COMPRESS_MODAL_Z}
          getContainer={() => document.body}
          onCompress={() => resolveCompressChoice(true)}
          onUploadOriginal={() => resolveCompressChoice(false)}
          onCancel={() => resolveCompressChoice(null)}
        />

        <Form form={form} layout="vertical" requiredMark={true} initialValues={{ isActive: 'true' }}>
          <Form.Item
            label={<span className="ctd-label">Name</span>}
            name="name"
            rules={[{ required: true, message: 'Enter template name' }]}
          >
            <Input placeholder="Enter template name" />
          </Form.Item>

          <Form.Item
            label={<span className="ctd-label">Active</span>}
            name="isActive"
            rules={[{ required: true, message: 'Select status' }]}
          >
            <Select placeholder="Select status">
              <Select.Option value="true">Active</Select.Option>
              <Select.Option value="false">Inactive</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={<span className="ctd-label">Categories</span>}
            name="categories"
            rules={[{ required: true, message: 'Select categories' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select categories"
              showArrow
              className="ctd-categories-select"
              tagRender={tagRender}
              maxTagCount={5}
              maxTagPlaceholder={selected => `+${selected.length} more`}
            >
              {(categories || []).map(cat => (
                <Select.Option key={cat} value={cat}>
                  {cat}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label={<span className="ctd-label">Thumbnail</span>}>
            <Upload
              accept="image/*"
              listType="picture"
              fileList={fileList}
              beforeUpload={beforeUpload}
              onChange={handleUploadChange}
              maxCount={1}
              disabled={isThumbnailUploading}
            >
              {fileList.length === 0 && (
                <div className="ctd-upload-placeholder"><PlusOutlined style={{ marginRight: 6, fontSize: 11 }} />Upload thumbnail image</div>
              )}
            </Upload>
          </Form.Item>
        </Form>

        <div style={{ paddingTop: 8 }}>
          {FooterBar}
        </div>
      </div>
    </Drawer>
  );
}
